import GsuLoader from '@/components/GsuLoader'
import IcoMoon, { IconNames } from '@/components/IcoMoon'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { coreLayoutContainer, coreLayoutSection } from '@/constants/measured-scope'
import { MessageType, useAi } from '@/context/ai'
import { AIElementNames, eventsTracker } from '@/lib/eventsTracker'
import { PartnerAssistantPrompt } from '@/shared/api-generated-types'
import InfoModal from '@/widgets/AI/InfoModal'
import getConfig from 'next/config'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { gql, useQuery } from 'urql'
import * as styles from './style'

interface NavigationLink extends PartnerAssistantPrompt {
  onClick?: () => void
}

const QUERY_GET_HOMEPAGEPROMPTS = gql`
  query GetPartnerAssistantPrompts($partnerId: ID!) {
    partnerAssistantPrompts(partnerId: $partnerId) {
      order
      label
      icon
    }
  }
`

interface Props {
  mode: 'WIDGET' | 'PAGE'
  partnerId?: string
}

const Search = (props: Props): JSX.Element => {
  const router = useRouter()
  const { dispatch } = useAi()
  const [searchQuery, setSearchQuery] = useState('')
  const config = getConfig()
  const partnerID = props.partnerId || config.publicRuntimeConfig.defaultPartnerId //GSU default

  const [{ data, fetching }] = useQuery<{
    partnerAssistantPrompts: PartnerAssistantPrompt[]
  }>({
    query: QUERY_GET_HOMEPAGEPROMPTS,
    variables: { partnerId: partnerID },
    requestPolicy: 'cache-first',
  })

  const [navigationLinks, setNavigationLinks] = useState<NavigationLink[]>([])
  const [visibleNavigationLinks, setVisibleNavigationLinks] = useState<number>(6) //To start with: top 6 prompts
  const [showDialog, setShowDialog] = useState<boolean>(false)

  useEffect(() => {
    if (fetching || !data?.partnerAssistantPrompts) return
    setNavigationLinks(data.partnerAssistantPrompts)
  }, [data?.partnerAssistantPrompts, fetching])

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value)
  }

  const handleSubmit = (): void => {
    if (!searchQuery.trim()) return // Prevent submission if searchQuery is empty
    eventsTracker.handleOnClickAndTrackEvent(AIElementNames.BUTTON_SEND_MESSAGE)
    dispatch({ type: 'UPDATE', payload: { query: searchQuery, messageType: MessageType.UserFreeText } })
    if (props.mode === 'WIDGET') router.push(`/helen`)
  }

  const handleShowMoreNavigationLinks = (): void => {
    setVisibleNavigationLinks((prevVisibleLinks) => prevVisibleLinks + 10)
  }

  const handleDialogOpen = (): void => {
    setShowDialog(!showDialog)
  }

  return (
    <div className={`w-full text-[var(--gsu-primary)] ${props.mode === 'WIDGET' ? '!p-0' : coreLayoutSection}`}>
      <div className={coreLayoutContainer}>
        {props.mode === 'PAGE' && (
          <>
            <h1 className={styles.title}>Hi, I’m Helen, your AI Assistant that can do amazing things!</h1>
            <p className="text-[16px] leading-[24px]">
              I'm here to help you get answers to your questions, find classes, explore articles, community groups, and
              more.{' '}
              <button className={styles.showDialogButton} onClick={handleDialogOpen}>
                Show me what else I can do with AI
              </button>
            </p>
          </>
        )}
        {showDialog && <InfoModal handleDialogOpen={handleDialogOpen} />}
        <div className={styles.instructionsContainer}>
          {props.mode === 'WIDGET' ? (
            <>
              <h2 className={styles.instructionsHeader}>
                Have you met Helen? She's our AI Assistant and can do amazing things!
              </h2>
              <p className="text-[16px] leading-[24px]">
                Find classes, articles, resources, answers and more.{' '}
                <Link
                  className="text-secondary underline font-bold"
                  href="/series/meet-helen-your-ai-assistant-on-getsetup"
                >
                  Take a class to learn how to use Helen.
                </Link>
              </p>
            </>
          ) : (
            <>
              <h2 className="font-[700] text-[16px] leading-[24px] mt-4">To Get Started:</h2>
              <p className="text-[16px] leading-[24px]">Type your question or task in the box below and hit enter!</p>
            </>
          )}
        </div>
        <div className={`${styles.inputContainer} ${props.mode === 'WIDGET' ? 'mt-0' : 'mt-5'}`}>
          <input
            autoComplete="off"
            placeholder="Type in any question here"
            type="text"
            onChange={handleOnChange}
            className={styles.chatInput}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSubmit()
              }
            }}
          />
          <button onClick={() => handleSubmit()} className={styles.sendButton}>
            <IcoMoon icon="send" size={24} />
          </button>
        </div>
        <div className={`${styles.navigationLinksContainer} ${props.mode === 'WIDGET' ? 'md:mt-5' : 'mb-4 mt-5'}`}>
          {fetching ? (
            <GsuLoader className="left-1/2" />
          ) : (
            navigationLinks.length > 0 && (
              <ScrollArea className="whitespace-nowrap">
                <p className="text-[var(--gsu-grey)] text-[16px] leading-[24px] mt-2 mb-2 md:mt-0">
                  Or, click on one of the suggested questions below.
                </p>
                <div className={styles.gridContainer}>
                  {navigationLinks.slice(0, visibleNavigationLinks).map((link, index) => (
                    <div
                      key={index}
                      className={styles.navigationLink}
                      onClick={() => {
                        eventsTracker.handleOnClickAndTrackEvent(AIElementNames.BUTTON_NIMDA_PROMPTS)
                        dispatch({
                          type: 'UPDATE',
                          payload: { query: link.label, messageType: MessageType.UserStaticPrompt },
                        })
                        if (props.mode === 'WIDGET') router.push('/helen')
                      }}
                    >
                      <IcoMoon icon={(link.icon as IconNames) || 'guide'} size={20} />
                      <span className="text-[16px] leading-[24px]">{link.label}</span>
                    </div>
                  ))}
                </div>
                {props.mode === 'PAGE' && visibleNavigationLinks < navigationLinks.length && (
                  <button onClick={handleShowMoreNavigationLinks} className={`${styles.showMoreButton} mt-2`}>
                    Show me more examples
                  </button>
                )}
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            )
          )}
        </div>
      </div>
    </div>
  )
}

export default Search
